import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { DashboardUseCase, useListDashboardsQuery } from 'generated/graphql';
import { useEffect, useMemo } from 'react';
import { useFetchMore } from 'v2/core/repository/edge-server/utils/records';
import { useGetUser } from 'v2/core/services/user';
import { EditableDashboardListItem } from '../types';

interface FetchEditableDashboardsOptions {
  query?: string;
}

export const useFetchEditableDashboards = ({
  query,
}: FetchEditableDashboardsOptions = {}) => {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const {
    data,
    loading,
    error,
    fetchMore: eFetchMore,
  } = useListDashboardsQuery({
    variables: {
      tenantID,
      userID,
      filter: {
        query,
        editableBy: userID,
        useCase: DashboardUseCase.Default,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const [isLoadingMore, fetchMore] = useFetchMore(
    eFetchMore,
    data?.listDashboards?.nextCursor,
  );

  const { showErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: 'Failed to get Dashboard list',
        props: {},
      });
    }
  }, [error, showErrorNotification]);

  const editableDashboards = useMemo(
    () =>
      data?.listDashboards?.dashboards?.map(
        (dashboard): EditableDashboardListItem => ({
          ...dashboard,
          // edit token should always be present since we only list dashboards
          // created by user. this is just for type safety.
          editToken: dashboard.editToken ?? '',
        }),
      ) ?? [],
    [data],
  );

  return {
    editableDashboards,
    loading: loading || isLoadingMore,
    error,
    fetchMore,
    hasNextPage: !!data?.listDashboards?.nextCursor,
  } as const;
};
